/**
 * useTimeTracker Hook
 *
 * Provides easy access to time tracking functionality with
 * additional utilities for common use cases.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTimeTrackerContext } from './TimeTrackerProvider'

/**
 * Main hook for accessing time tracker
 */
export const useTimeTracker = () => {
  return useTimeTrackerContext()
}

/**
 * Hook that provides live-updating current page duration
 * Updates every second for real-time display
 */
export const useLivePageDuration = (updateInterval = 1000) => {
  const { getCurrentPageDuration, getFormattedDuration, currentVisit } = useTimeTrackerContext()
  const [duration, setDuration] = useState(0)
  const [formattedDuration, setFormattedDuration] = useState('0s')

  useEffect(() => {
    // Initial update
    const updateDuration = () => {
      const ms = getCurrentPageDuration()
      setDuration(ms)
      setFormattedDuration(getFormattedDuration(ms))
    }

    updateDuration()

    // Set up interval for live updates
    const intervalId = setInterval(updateDuration, updateInterval)

    return () => clearInterval(intervalId)
  }, [getCurrentPageDuration, getFormattedDuration, updateInterval, currentVisit])

  return {
    duration,
    formattedDuration,
    currentVisit,
  }
}

/**
 * Hook that provides live-updating total session duration
 */
export const useLiveSessionDuration = (updateInterval = 1000) => {
  const { totalSessionTime, sessionStartedAt, getFormattedDuration, getCurrentPageDuration } = useTimeTrackerContext()
  const [totalDuration, setTotalDuration] = useState(0)
  const [formattedDuration, setFormattedDuration] = useState('0s')

  useEffect(() => {
    const updateDuration = () => {
      // Total = completed visits + current page time
      const currentPageTime = getCurrentPageDuration()
      const total = totalSessionTime + currentPageTime
      setTotalDuration(total)
      setFormattedDuration(getFormattedDuration(total))
    }

    updateDuration()
    const intervalId = setInterval(updateDuration, updateInterval)

    return () => clearInterval(intervalId)
  }, [totalSessionTime, sessionStartedAt, getFormattedDuration, getCurrentPageDuration, updateInterval])

  return {
    totalDuration,
    formattedDuration,
    sessionStartedAt,
  }
}

/**
 * Hook for getting area breakdown with live updates
 */
export const useAreaBreakdown = () => {
  const { areaBreakdown, currentVisit, getCurrentPageDuration, getFormattedDuration } = useTimeTrackerContext()
  const [liveBreakdown, setLiveBreakdown] = useState<Record<string, { ms: number; formatted: string }>>({})

  useEffect(() => {
    const updateBreakdown = () => {
      const breakdown: Record<string, { ms: number; formatted: string }> = {}

      // Add completed visit times
      Object.entries(areaBreakdown).forEach(([area, ms]) => {
        breakdown[area] = { ms, formatted: getFormattedDuration(ms) }
      })

      // Add current page time to its area
      if (currentVisit) {
        const currentTime = getCurrentPageDuration()
        const existingTime = breakdown[currentVisit.area]?.ms || 0
        const totalTime = existingTime + currentTime
        breakdown[currentVisit.area] = {
          ms: totalTime,
          formatted: getFormattedDuration(totalTime),
        }
      }

      setLiveBreakdown(breakdown)
    }

    updateBreakdown()
    const intervalId = setInterval(updateBreakdown, 1000)

    return () => clearInterval(intervalId)
  }, [areaBreakdown, currentVisit, getCurrentPageDuration, getFormattedDuration])

  // Sort by time spent (descending)
  const sortedBreakdown = Object.entries(liveBreakdown)
    .sort((a, b) => b[1].ms - a[1].ms)
    .map(([area, data]) => ({ area, ...data }))

  return {
    breakdown: liveBreakdown,
    sortedBreakdown,
  }
}

/**
 * Hook for tracking time on a specific component/section
 * Useful for tracking time spent on sub-sections within a page
 */
export const useComponentTimeTracker = (componentName: string) => {
  const [enteredAt] = useState(() => Date.now())
  const [duration, setDuration] = useState(0)
  const { getFormattedDuration } = useTimeTrackerContext()

  useEffect(() => {
    const updateDuration = () => {
      setDuration(Date.now() - enteredAt)
    }

    const intervalId = setInterval(updateDuration, 1000)
    return () => clearInterval(intervalId)
  }, [enteredAt])

  const getReport = useCallback(() => {
    return {
      componentName,
      enteredAt,
      duration,
      formattedDuration: getFormattedDuration(duration),
    }
  }, [componentName, enteredAt, duration, getFormattedDuration])

  return {
    duration,
    formattedDuration: getFormattedDuration(duration),
    getReport,
  }
}

export default useTimeTracker
